import { Observable, OperatorFunction, from, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';

export interface JoinObservableWithArrayMultipleOptions<InputKey, OutputKey> {
    inputKey?: InputKey;
    outputKey?: OutputKey;
}

/**
 * Joins an observable with the output of a mapping function with multiple output values
 *
 * @param outputArrayMappingFunc - The output array values function
 * @param joinFunc - The join boolean function
 * @param options - The options to change the keys
 * @returns - The observable with an input and output that are shared
 */
export function joinObservableWithArrayMultiple<
    InputType,
    OutputType,
    InputKey extends string = 'input',
    OutputKey extends string = 'output',
    R extends {
        [key in InputKey]: InputType;
    } & {
        [key in OutputKey]: OutputType[];
    } = {
        [key in InputKey]: InputType;
    } & {
        [key in OutputKey]: OutputType[];
    },
>(
    outputArrayMappingFunc: (value: InputType[], index: number) => Observable<OutputType[]> | Promise<OutputType[]>,
    joinFunc: (input: InputType, output: OutputType) => boolean,
    options?: JoinObservableWithArrayMultipleOptions<InputKey, OutputKey>,
): OperatorFunction<InputType, R> {
    return source$ =>
        source$.pipe(
            toArray(),
            mergeMap(input =>
                of(input).pipe(
                    mergeMap(outputArrayMappingFunc),
                    map(output => ({
                        input,
                        output,
                    })),
                ),
            ),
            mergeMap(({ input: inputArray, output }) =>
                from(inputArray).pipe(
                    map(
                        input =>
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            ({
                                [options?.inputKey ?? 'input']: input,
                                [options?.outputKey ?? 'output']: output.filter(out => joinFunc(input, out)),
                            }) as R,
                    ),
                ),
            ),
        );
}
